import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { Container } from "components";
import Breadcrumb from "components/molecules/Breadcrumb";
import { BiTimeFive, BiHelpCircle } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { getData, putData } from "actions";
import * as Yup from "yup";
import { API_URL_ticketPriority, API_URL_ticketSetting } from "constants/api";
import { ticketSettingReducers } from "redux/ticketSettingSlice";
import { ticketPriorityReducers } from "redux/ticketPrioritySlice";
import { Tooltip } from "react-tooltip";
import { placeholderToZero } from "utils/placeholderToZero";
import { AuthContext } from "context/AuthContext";

const Ticket = () => {
  const navigate = useNavigate();
  const [adminAccess, setAdminAccess] = useState(false);
  const { datauser } = useContext(AuthContext);
  const dispatch = useDispatch();
  const inputRefs = useRef([]);

  const {
    getTicketSettingResult,
    getTicketPriorityResult,
    addTicketSettingResult,
  } = useSelector((state) => ({
    getTicketSettingResult: state.ticketSetting.getTicketSettingResult,
    getTicketPriorityResult: state.ticketPriority.getTicketPriorityResult,
    addTicketSettingResult: state.ticketSetting.addTicketSettingResult,
  }));

  const formik = useFormik({
    initialValues: {
      num_format: "",
    },
    validationSchema: Yup.object({
      num_format: Yup.string().required("Format is required"),
    }),
    onSubmit: (values) => {
      const formData = new FormData();
      formData.append("num_format", formik.values.num_format);
      //edit on id 1
      putData(
        `${API_URL_ticketSetting}1/`,
        formData,
        { dispatch, redux: ticketSettingReducers },
        "ADD_TICKETSETTING",
        () => {
          fetchData();
        }
      );
    },
  });

  const get = useCallback(
    async (params) => {
      getData(
        API_URL_ticketSetting,
        params,
        { dispatch, redux: ticketSettingReducers },
        "GET_TICKETSETTING"
      );
    },
    [dispatch]
  );

  const fetchData = useCallback(() => {
    //get id 1 for setting
    const params = `1/`;
    get(params);
  }, [get]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (addTicketSettingResult) {
      fetchData();
    }
  }, [addTicketSettingResult, fetchData]);

  useEffect(() => {
    if (getTicketSettingResult) {
      formik.setFieldValue("num_format", getTicketSettingResult.num_format);
    }
  }, [getTicketSettingResult, getTicketPriorityResult]);

  // Focus Input validate & submit
  const handleFormSubmit = async (event) => {
    event.preventDefault();
    await formik.validateForm();
    const firstInvalidInputRef = inputRefs.current.find((inputRef) => {
      const fieldName = inputRef.name;
      formik.setFieldTouched(fieldName, true, true);
      return formik.errors[fieldName];
    });
    if (firstInvalidInputRef) {
      firstInvalidInputRef.focus();
    } else {
      formik.handleSubmit();
    }
  };

  useEffect(() => {
    if (datauser !== "") {
      const permissionViewMenuAdmin = datauser.group_permissions.includes(
        "access_menu_admin"
      );
      if (permissionViewMenuAdmin) {
        setAdminAccess(true);
      } else {
        navigate("/dashboard/agent");
      }
    }
  }, [datauser]);

  if (!adminAccess) {
    return null;
  }

  return (
    <Container>
      <Breadcrumb
        title={"Settings"}
        excludedPaths={["tickets", "administrator"]}
        group={"Superadmin"}
      />
      <div className="bg-neutral dark:bg-neutral-800 rounded overflow-hidden shadow">
        <div className="h-12 w-full grid items-center px-4 bg-neutral-100 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600 text-xs md:text-base">
          Ticket settings
        </div>
        <form onSubmit={handleFormSubmit}>
          <div className="h-auto w-auto px-4 bg-neutral dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-600 object-left">
            <div className="mt-4 grid grid-cols-12 gap-4">
              <div className="col-span-full mb-1.5 md:col-span-6 md:col-start-1 lg:col-span-6 lg:col-start-1">
                <label className="flex mb-2 items-center font-semibold">
                  Format{" "}
                  <BiHelpCircle
                    className="ml-2 bg-blue-500 rounded-full text-white"
                    data-tooltip-id="format"
                    data-tooltip-content="This setting is used to generate ticket numbers. Use hash signs (#) where digits are to be placed Any other text in the number format will be reserved."
                    data-tooltip-place="left"
                  />
                </label>
                <input
                  type="text"
                  name="num_format"
                  id="num_format"
                  value={formik.values.num_format}
                  onChange={formik.handleChange}
                  ref={(ref) => (inputRefs.current[0] = ref)}
                  className="block w-full rounded-md border border-gray-500 bg-white py-1.5 px-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:ring-1 focus:ring-blue-400 sm:text-sm sm:leading-6"
                />
                {formik.touched.num_format && formik.errors.num_format && (
                  <div className="text-red-500 dark:text-red-300 text-xs">
                    {formik.errors.num_format}
                  </div>
                )}
                <div className="flex my-4">
                  <span>Number :</span>
                  <span className="font-bold">
                    {placeholderToZero(formik.values.num_format)}
                  </span>
                </div>
              </div>
            </div>
          </div>
          <div className="h-12 w-full grid items-center px-4 bg-neutral-50 dark:bg-neutral-700 border-b border-neutral-200 dark:border-neutral-600">
            <button
              type="submit"
              className="w-20 items-center p-1 px-3 rounded shadow-sm border text-white bg-blue-800 hover:bg-blue-700"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
      <Tooltip id="format" />
      <Tooltip id="type" />
    </Container>
  );
};

export default Ticket;
